import {
  useCreateOrderShift,
  useDeleteOrderShift,
  useOrderCapacityMode,
  useOrderShifts,
  useUpdateOrderShift,
} from '../hooks/useOrderSetup';
import { ChipInput } from '@/components/ChipInput';
import { useT } from '@/i18n';
import { LeadTimeDiagram } from '../components/LeadTimeDiagram';
import { useDeliveryLeg } from '../hooks/useDeliveryLeg';
import { useOpeningMinutes } from '../hooks/useOpeningMinutes';
import type { OrderShift, OrderShiftRules } from '@restaurant/api-client';
import { DEFAULT_PREP_MINUTES } from '@/features/orders/utils/time';
import {
  Block,
  BlockFooter,
  BlockTitle,
  Button,
  List,
  ListButton,
  ListInput,
  ListItem,
  Navbar,
  NavRight,
  Link as F7Link,
  Page,
  Preloader,
  f7,
} from 'framework7-react';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { useEffect, useMemo, useState } from 'react';

type OrderType = 'PICKUP' | 'DELIVERY';
type ShiftMode = 'SIMPLE' | 'ADVANCED';

// Order matches the on-screen day picker; `value` indexes into
// t.orderCapacity.dayNamesShort/dayNamesLong (0=Sun … 6=Sat, i.e. Date.getDay()).
const DAY_VALUES = [1, 2, 3, 4, 5, 6, 0];

type DraftState = {
  mode: ShiftMode;
  label: string;
  startTime: string;
  endTime: string;
  dayOfWeek: number[];
  isActive: boolean;
  // Capacity: '' = inherit the default (stored null, live-inherited).
  maxOrdersPerSlot: string;
  // ADVANCED only.
  slotMinutes: string;
  // ADVANCED only; '' = cap window equals the slot interval.
  capacityWindow: string;
  // Shared, nullable=inherit on a WINDOW.
  prepTimeMinutes: string;
  schedulingLeadMinutes: string;
};

/** Derive editable form state from a WINDOW shift, or `null` if not loaded /
 *  not a WINDOW. Used as the lazy `useState` initializer so the first render
 *  is already the populated form - critical for the F7 push transition. */
function deriveDraft(shift: OrderShift | undefined): DraftState | null {
  if (!shift || shift.scope !== 'WINDOW') return null;
  return {
    mode: shift.rules.mode,
    label: shift.label,
    startTime: shift.startTime,
    endTime: shift.endTime,
    dayOfWeek: shift.dayOfWeek,
    isActive: shift.isActive,
    maxOrdersPerSlot:
      shift.rules.maxOrdersPerSlot != null ? String(shift.rules.maxOrdersPerSlot) : '',
    slotMinutes: shift.rules.mode === 'ADVANCED' ? String(shift.rules.slotMinutes) : '',
    capacityWindow:
      shift.rules.mode === 'ADVANCED' && shift.rules.capacityWindowMinutes != null
        ? String(shift.rules.capacityWindowMinutes)
        : '',
    prepTimeMinutes: shift.prepTimeMinutes != null ? String(shift.prepTimeMinutes) : '',
    schedulingLeadMinutes:
      shift.schedulingLeadMinutes != null ? String(shift.schedulingLeadMinutes) : '',
  };
}

function blankDraft(mode: ShiftMode): DraftState {
  return {
    mode,
    label: '',
    startTime: '11:00',
    endTime: '21:00',
    dayOfWeek: [],
    isActive: true,
    maxOrdersPerSlot: '',
    slotMinutes: '',
    capacityWindow: '',
    prepTimeMinutes: '',
    schedulingLeadMinutes: '',
  };
}

/** "" → null, "12" → 12, else NaN (caller treats as invalid). */
function parseCap(v: string): number | null | typeof NaN {
  const t = v.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isInteger(n) && n > 0 ? n : NaN;
}

/** "" → null, "0"+ → n, else NaN (prep / notice). */
function parseLead(v: string): number | null | typeof NaN {
  const t = v.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isInteger(n) && n >= 0 ? n : NaN;
}

export default function OrderShiftFormPage({
  f7route,
}: {
  f7route: { params: { shiftId?: string; orderType?: string } };
}) {
  const { shiftId } = f7route.params;
  const editing = !!shiftId;
  const t = useT();
  const paramType: OrderType = f7route.params.orderType === 'DELIVERY' ? 'DELIVERY' : 'PICKUP';

  // When editing we need the shift's own channel to load the right list; when
  // creating, the channel comes from the route param.
  const { data: pickupShifts } = useOrderShifts('PICKUP', true);
  const { data: deliveryShifts } = useOrderShifts('DELIVERY', true);
  const allShifts = useMemo(
    () => [...(pickupShifts ?? []), ...(deliveryShifts ?? [])],
    [pickupShifts, deliveryShifts],
  );
  const shift = editing ? allShifts.find((s) => s.id === shiftId) : undefined;
  const relType: OrderType = editing ? (shift?.orderType ?? paramType) : paramType;

  // Active capacity mode for the channel - drives a NEW shift's rule shape.
  const { data: capacity } = useOrderCapacityMode(relType);
  const activeMode: ShiftMode | undefined = capacity?.shiftMode;
  // Delivery's preview must show the drive too — "ready" is when the food leaves
  // the kitchen, not when the guest has it.
  const deliveryLeg = useDeliveryLeg(relType);
  const openingMinutes = useOpeningMinutes(relType);

  // The channel's default (BASELINE) - its concrete numbers are shown as the
  // gray placeholders so a blank field reads as "= this default", not "empty".
  const baseline = useMemo(
    () => allShifts.find((s) => s.scope === 'BASELINE' && s.orderType === relType),
    [allShifts, relType],
  );
  const ph = {
    maxOrders:
      baseline && baseline.rules.maxOrdersPerSlot != null
        ? String(baseline.rules.maxOrdersPerSlot)
        : t.orderCapacity.noCap,
    slotMinutes: baseline?.rules.mode === 'ADVANCED' ? String(baseline.rules.slotMinutes) : '30',
    // What a blank capacity window inherits: the baseline's own window, or its
    // slot when it has none, falling back to the hourly default.
    capacityWindow:
      baseline?.rules.mode === 'ADVANCED'
        ? String(baseline.rules.capacityWindowMinutes ?? baseline.rules.slotMinutes)
        : '60',
    prep: String(baseline?.prepTimeMinutes ?? DEFAULT_PREP_MINUTES),
    lead: baseline?.schedulingLeadMinutes != null ? String(baseline.schedulingLeadMinutes) : '0',
  };

  const create = useCreateOrderShift();
  const update = useUpdateOrderShift();
  const del = useDeleteOrderShift();

  const [draft, setDraft] = useState<DraftState | null>(() =>
    editing ? deriveDraft(shift) : activeMode ? blankDraft(activeMode) : null,
  );

  // Cache-miss path: initialize once the shift / mode arrives.
  useEffect(() => {
    if (draft) return;
    if (editing && shift) setDraft(deriveDraft(shift));
    if (!editing && activeMode) setDraft(blankDraft(activeMode));
  }, [draft, editing, shift, activeMode]);

  const back = () => f7.views.current.router.back();

  const toggleDay = (dow: number) =>
    setDraft((d) =>
      d
        ? {
            ...d,
            dayOfWeek: d.dayOfWeek.includes(dow)
              ? d.dayOfWeek.filter((x) => x !== dow)
              : [...d.dayOfWeek, dow].sort((a, b) => a - b),
          }
        : d,
    );

  const sf = t.orderSetup.shiftForm;

  const submit = () => {
    if (!draft) return;
    const label = draft.label.trim();
    if (!label) return f7.dialog.alert(sf.nameRequired);
    if (draft.dayOfWeek.length === 0) return f7.dialog.alert(sf.dayRequired);
    if (
      !/^\d{2}:\d{2}$/.test(draft.startTime) ||
      !/^\d{2}:\d{2}$/.test(draft.endTime) ||
      draft.startTime >= draft.endTime
    ) {
      return f7.dialog.alert(sf.timeInvalid);
    }

    const maxOrders = parseCap(draft.maxOrdersPerSlot);
    if (Number.isNaN(maxOrders)) {
      return f7.dialog.alert(sf.capacityInvalid);
    }
    let rules: OrderShiftRules;
    if (draft.mode === 'ADVANCED') {
      const slot = parseCap(draft.slotMinutes);
      if (slot == null || Number.isNaN(slot)) {
        return f7.dialog.alert(sf.slotInvalid);
      }
      // Blank window = cap per booking slot; a value must be a whole multiple of it.
      const window = parseCap(draft.capacityWindow);
      if (
        Number.isNaN(window) ||
        (typeof window === 'number' &&
          (window < (slot as number) || window % (slot as number) !== 0))
      ) {
        return f7.dialog.alert(t.orderCapacity.capacityWindowAlert);
      }
      rules = {
        mode: 'ADVANCED',
        slotMinutes: slot as number,
        maxOrdersPerSlot: maxOrders as number | null,
        ...(typeof window === 'number' ? { capacityWindowMinutes: window } : {}),
      };
    } else {
      rules = { mode: 'SIMPLE', slotMinutes: 60, maxOrdersPerSlot: maxOrders as number | null };
    }

    const prep = parseLead(draft.prepTimeMinutes);
    const lead = parseLead(draft.schedulingLeadMinutes);
    if (Number.isNaN(prep) || Number.isNaN(lead)) {
      return f7.dialog.alert(sf.leadInvalid);
    }

    const onError = (e: unknown) => f7.dialog.alert(e instanceof Error ? e.message : sf.saveFailed);
    const done = () => {
      f7.toast.show({ text: t.saved, closeTimeout: 2000, position: 'bottom' });
      back();
    };

    if (editing && shiftId) {
      update.mutate(
        {
          shiftId,
          data: {
            label,
            startTime: draft.startTime,
            endTime: draft.endTime,
            dayOfWeek: draft.dayOfWeek,
            isActive: draft.isActive,
            rules,
            prepTimeMinutes: prep as number | null,
            schedulingLeadMinutes: lead as number | null,
          },
        },
        { onSuccess: done, onError },
      );
    } else {
      create.mutate(
        {
          scope: 'WINDOW',
          orderType: relType,
          label,
          startTime: draft.startTime,
          endTime: draft.endTime,
          dayOfWeek: draft.dayOfWeek,
          isActive: draft.isActive,
          rules,
          prepTimeMinutes: prep as number | null,
          schedulingLeadMinutes: lead as number | null,
        },
        { onSuccess: done, onError },
      );
    }
  };

  const remove = () => {
    if (!shiftId) return;
    f7.dialog.confirm(sf.deleteConfirm, t.delete, () => {
      del.mutate(shiftId, {
        onSuccess: () => {
          f7.toast.show({ text: sf.deleted, closeTimeout: 2000, position: 'bottom' });
          back();
        },
        onError: (e) => f7.dialog.alert(e instanceof Error ? e.message : sf.deleteFailed),
      });
    });
  };

  const saving = create.isPending || update.isPending;
  const channel =
    relType === 'DELIVERY'
      ? t.orderSetup.page.delivery.toLowerCase()
      : t.orderSetup.page.pickup.toLowerCase();

  return (
    <Page name="order-shift-form">
      <Navbar title={editing ? sf.editSpecialHours : sf.newSpecialHours} backLink={t.back}>
        <NavRight>
          <F7Link onClick={saving || !draft ? undefined : submit}>{t.saveShort}</F7Link>
        </NavRight>
      </Navbar>

      {!draft ? (
        <Block className="text-align-center" style={{ paddingTop: 48 }}>
          <Preloader />
        </Block>
      ) : (
        <>
          <BlockTitle>{sf.name}</BlockTitle>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListInput
              type="text"
              placeholder={sf.namePlaceholder}
              value={draft.label}
              onInput={(e) =>
                setDraft((d) => (d ? { ...d, label: (e.target as HTMLInputElement).value } : d))
              }
            />
          </List>

          <BlockTitle>{sf.days}</BlockTitle>
          <BlockFooter style={{ marginTop: -6 }}>{sf.daysFooter(channel)}</BlockFooter>
          <Block strongIos strongMd insetIos insetMd>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {DAY_VALUES.map((value) => {
                const on = draft.dayOfWeek.includes(value);
                return (
                  <Button
                    key={value}
                    small
                    round
                    fill={on}
                    outline={!on}
                    onClick={() => toggleDay(value)}
                    aria-pressed={on}
                    aria-label={t.orderCapacity.dayNamesLong[value]}
                    style={{ minWidth: 44 }}
                  >
                    {t.orderCapacity.dayNamesShort[value]}
                  </Button>
                );
              })}
            </div>
          </Block>

          <BlockTitle>{sf.hours}</BlockTitle>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListItem noChevron title={sf.opens}>
              <ChipInput
                slot="after"
                type="time"
                value={draft.startTime}
                onChange={(v) => setDraft((d) => (d ? { ...d, startTime: v } : d))}
              />
            </ListItem>
            <ListItem noChevron title={sf.closes}>
              <ChipInput
                slot="after"
                type="time"
                value={draft.endTime}
                onChange={(v) => setDraft((d) => (d ? { ...d, endTime: v } : d))}
              />
            </ListItem>
          </List>

          <BlockTitle>{sf.capacity}</BlockTitle>
          <BlockFooter style={{ marginTop: -6 }}>{sf.capacityFooter}</BlockFooter>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListInput
              label={
                draft.mode === 'ADVANCED'
                  ? t.orderCapacity.ordersPerSlotLabel
                  : t.orderCapacity.maxOrdersPerHourLabel
              }
              type="number"
              placeholder={ph.maxOrders}
              value={draft.maxOrdersPerSlot}
              onInput={(e) =>
                setDraft((d) =>
                  d ? { ...d, maxOrdersPerSlot: (e.target as HTMLInputElement).value } : d,
                )
              }
            />
            {draft.mode === 'ADVANCED' && (
              <ListInput
                label={t.orderCapacity.slotLengthLabel}
                type="number"
                placeholder={ph.slotMinutes}
                value={draft.slotMinutes}
                onInput={(e) =>
                  setDraft((d) =>
                    d ? { ...d, slotMinutes: (e.target as HTMLInputElement).value } : d,
                  )
                }
              />
            )}
            {draft.mode === 'ADVANCED' && (
              <ListInput
                label={t.orderCapacity.capacityWindowLabel}
                type="number"
                // Blank = inherit the channel default's capacity window.
                placeholder={ph.capacityWindow}
                value={draft.capacityWindow}
                onInput={(e) =>
                  setDraft((d) =>
                    d ? { ...d, capacityWindow: (e.target as HTMLInputElement).value } : d,
                  )
                }
              />
            )}
            <ListInput
              label={t.orderCapacity.prepTimeLabel}
              type="number"
              placeholder={ph.prep}
              value={draft.prepTimeMinutes}
              onInput={(e) =>
                setDraft((d) =>
                  d ? { ...d, prepTimeMinutes: (e.target as HTMLInputElement).value } : d,
                )
              }
            />
            <ListInput
              label={t.orderCapacity.minNoticeLabel}
              type="number"
              placeholder={ph.lead}
              value={draft.schedulingLeadMinutes}
              onInput={(e) =>
                setDraft((d) =>
                  d ? { ...d, schedulingLeadMinutes: (e.target as HTMLInputElement).value } : d,
                )
              }
            />
          </List>

          <BlockTitle>{sf.preview}</BlockTitle>
          {(() => {
            // Blank field = inherits the channel default (the gray placeholder),
            // so the preview reflects what actually applies on this rule's days.
            const pPrep = parseLead(draft.prepTimeMinutes);
            const pNotice = parseLead(draft.schedulingLeadMinutes);
            const prepMinutes =
              typeof pPrep === 'number' && !Number.isNaN(pPrep) ? pPrep : Number(ph.prep) || 0;
            const noticeMinutes =
              typeof pNotice === 'number' && !Number.isNaN(pNotice)
                ? pNotice
                : Number(ph.lead) || 0;
            return (
              <LeadTimeDiagram
                prepMinutes={prepMinutes}
                noticeMinutes={noticeMinutes}
                openingBufferMinutes={baseline?.openingBufferMinutes ?? 0}
                openingMinutes={openingMinutes}
                delivery={deliveryLeg}
              />
            );
          })()}

          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListItem title={sf.active}>
              <ToggleSwitch
                slot="after"
                checked={draft.isActive}
                onChange={(v) => setDraft((d) => (d ? { ...d, isActive: v } : d))}
              />
            </ListItem>
          </List>
          <BlockFooter>{sf.activeFooter}</BlockFooter>

          {editing && (
            <List strongIos strongMd insetIos insetMd>
              <ListButton color="red" onClick={remove}>
                {sf.deleteRule}
              </ListButton>
            </List>
          )}
        </>
      )}
    </Page>
  );
}
